import React, { CSSProperties, useCallback, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { useInfiniteQuery } from "@tanstack/react-query";
import { createColumnHelper } from "@tanstack/react-table";
import debounce from "lodash/debounce";
import { DataTable } from "../../../../ui-components/data-table";
import Tag from "../../../../ui-components/molecules/tag/tag";
import Button, { ButtonSizes, ButtonThemes } from "../../../../ui-components/button/button";
import LabeledTextInput from "../../../../ui-components/molecules/labeled-inputs/labeled-text-input/labeledTextInput";
import { LabelModes } from "../../../../ui-components/molecules/labeled-inputs/labeledInput";
import XGSSelect, { XGSSelectOption } from "../../../../ui-components/xgs-select/xgsSelect";
import { QUERY_KEYS } from "../../../../app/data/common/queryKeys";
import { Routes } from "../../../../app/route/RoutesConfig";
import InvoiceFactoringService from "../../../../app/data/invoice-factoring/invoiceFactoringService";
import {
  DenimUploadBatchItem,
  DenimUploadBatchItemStatus,
  FactoringAttemptOutcome,
} from "../../../../app/data/invoice-factoring/models";
import {
  ATTEMPT_OUTCOME_COLORS,
  ATTEMPT_OUTCOME_LABELS,
  FACTORING_FILTER_DEBOUNCE_MS,
  FACTORING_ITEMS_PAGE_SIZE,
  FACTORING_QUERY_OPTIONS,
  ITEM_STATUS_COLORS,
  ITEM_STATUS_LABELS,
  formatDateTime,
} from "../constants";
import AttemptSteps from "../common/attemptSteps";

const invoiceFactoringService = InvoiceFactoringService.getInstance();

const columnHelper = createColumnHelper<DenimUploadBatchItem>();

const selectMenuStyles = {
  menuPortal: (base: CSSProperties) => ({ ...base, zIndex: 1100 }),
};

const StepOutcome: React.FC<{ outcome: FactoringAttemptOutcome | null }> = ({ outcome }) =>
  outcome ? (
    <Tag mods={{ color: ATTEMPT_OUTCOME_COLORS[outcome], uppercase: false }}>{ATTEMPT_OUTCOME_LABELS[outcome]}</Tag>
  ) : (
    <span className="xgs-invoice-factoring__muted">—</span>
  );

export interface BatchItemsTableProps {
  batchId: string;
  itemsByStatus?: Partial<Record<DenimUploadBatchItemStatus, number>>;
  terminalPrefix: string;
  refetchInterval: number | false;
}

const BatchItemsTable: React.FC<BatchItemsTableProps> = ({ batchId, itemsByStatus, terminalPrefix, refetchInterval }) => {
  const [searchInput, setSearchInput] = useState("");
  const [search, setSearch] = useState("");
  const [status, setStatus] = useState<DenimUploadBatchItemStatus | "">("");

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const debouncedSetSearch = useCallback(debounce(setSearch, FACTORING_FILTER_DEBOUNCE_MS), []);

  const statusOptions: XGSSelectOption[] = useMemo(
    () => [
      { value: "", label: "All rows" },
      ...(Object.keys(ITEM_STATUS_LABELS) as DenimUploadBatchItemStatus[]).map((value) => ({
        value,
        label: `${ITEM_STATUS_LABELS[value]} (${itemsByStatus?.[value] ?? 0})`,
      })),
    ],
    [itemsByStatus]
  );

  const { data, fetchNextPage, isFetching, isFetchingNextPage, isLoading, isPreviousData, isError, error } =
    useInfiniteQuery({
      queryKey: [QUERY_KEYS.INVOICE_FACTORING_BATCH_ITEMS, batchId, search, status],
      queryFn: ({ pageParam = 1 }) =>
        invoiceFactoringService.getBatchItems(batchId, {
          page: pageParam,
          pageSize: FACTORING_ITEMS_PAGE_SIZE,
          search: search || undefined,
          status: status || undefined,
        }),
      getNextPageParam: (lastPage) => lastPage.pagination?.next ?? undefined,
      keepPreviousData: true,
      retry: false,
      refetchInterval,
      ...FACTORING_QUERY_OPTIONS,
    });

  const items = useMemo(() => data?.pages.flatMap((page) => page.items) ?? [], [data]);
  const hasNextPage = !!data?.pages[data.pages.length - 1]?.pagination?.next;
  const total = data?.pages[0]?.pagination?.total ?? 0;

  const columns = useMemo(
    () => [
      columnHelper.display({
        id: "expand",
        header: "",
        cell: ({ row }) =>
          row.getCanExpand() ? (
            <button
              type="button"
              className="xgs-invoice-factoring__expand"
              aria-label={row.getIsExpanded() ? "Hide steps" : "Show steps"}
              onClick={row.getToggleExpandedHandler()}
            >
              <span className={row.getIsExpanded() ? "xgs-invoice-factoring__chevron--open" : "xgs-invoice-factoring__chevron"} />
            </button>
          ) : null,
        size: 44,
        minSize: 44,
      }),
      columnHelper.accessor("rowNumber", {
        id: "rowNumber",
        header: "Row",
        size: 70,
        minSize: 60,
      }),
      columnHelper.accessor("probillNumber", {
        id: "probillNumber",
        header: "Probill",
        cell: (info) =>
          info.getValue() ? (
            <Link
              to={`${terminalPrefix}${Routes.billing.invoiceFactoringInvoices}/${info.getValue()}`}
              className="xgs-table-link-cell"
              onClick={(e) => e.stopPropagation()}
            >
              {info.getValue()}
            </Link>
          ) : (
            <span className="xgs-invoice-factoring__muted">{info.row.original.rawValue || "—"}</span>
          ),
        size: 120,
        minSize: 100,
      }),
      columnHelper.accessor("status", {
        id: "status",
        header: "Status",
        cell: (info) => (
          <Tag mods={{ color: ITEM_STATUS_COLORS[info.getValue()], uppercase: false }}>
            {ITEM_STATUS_LABELS[info.getValue()]}
          </Tag>
        ),
        size: 150,
        minSize: 130,
      }),
      columnHelper.accessor("jobStepOutcome", {
        id: "jobStep",
        header: "Job creation",
        cell: (info) => <StepOutcome outcome={info.getValue()} />,
        size: 190,
        minSize: 160,
      }),
      columnHelper.accessor("documentStepOutcome", {
        id: "documentStep",
        header: "Document upload",
        cell: (info) => <StepOutcome outcome={info.getValue()} />,
        size: 190,
        minSize: 160,
      }),
      columnHelper.display({
        id: "denimJob",
        header: "Denim job",
        cell: ({ row }) => (
          <span className="xgs-invoice-factoring__mono">{row.original.factoredInvoice?.denimJobId || "—"}</span>
        ),
        size: 150,
        minSize: 120,
      }),
      columnHelper.accessor("errorMessage", {
        id: "errorMessage",
        header: "Details",
        cell: (info) => (
          <span className={info.getValue() ? "xgs-invoice-factoring__text--error" : "xgs-invoice-factoring__muted"} title={info.getValue() || ""}>
            {info.getValue() || info.row.original.factoredInvoice?.statusReason || "—"}
          </span>
        ),
        size: 320,
        minSize: 200,
      }),
      columnHelper.accessor("processedAt", {
        id: "processedAt",
        header: "Processed",
        cell: (info) => formatDateTime(info.getValue()),
        size: 170,
        minSize: 150,
      }),
    ],
    [terminalPrefix]
  );

  return (
    <div className="xgs-invoice-factoring__items">
      <div className="xgs-invoice-factoring__filters">
        <LabeledTextInput
          label="Search:"
          labelMode={LabelModes.column}
          type="text"
          value={searchInput}
          onChange={(e) => {
            const value = (e.target as HTMLInputElement).value;
            setSearchInput(value);
            debouncedSetSearch(value);
          }}
          placeholder="Probill number or raw value"
          className="xgs-invoice-factoring__filter-field xgs-invoice-factoring__filter-search"
        />
        <div className="xgs-invoice-factoring__filter-field xgs-invoice-factoring__filter-select">
          <span className="xgs-invoice-factoring__filter-label">Status:</span>
          <XGSSelect
            value={statusOptions.find((o) => o.value === status) ?? statusOptions[0]}
            options={statusOptions}
            onValueChange={(o) => setStatus((o?.value as DenimUploadBatchItemStatus) || "")}
            isClearable={false}
            menuPortalTarget={document.body}
            menuPosition="fixed"
            styles={selectMenuStyles}
          />
        </div>
        <span className="xgs-invoice-factoring__muted">
          Showing {items.length} of {total} rows. Expand a row to see each step and document.
        </span>
      </div>

      {isError && (
        <div className="xgs-invoice-factoring__error-box">Unable to load batch rows. {(error as Error)?.message}</div>
      )}

      <DataTable
        columns={columns}
        data={items}
        isLoading={isLoading || (isFetching && isPreviousData)}
        keepTableOnLoading
        rowHeight={48}
        noResultsText="No rows match the filters."
        getRowCanExpand={(row) => !!row.original.probillNumber}
        renderSubComponent={(row) => (
          <div className="xgs-invoice-factoring__expanded">
            <AttemptSteps attempts={(row.original as DenimUploadBatchItem).attempts} />
          </div>
        )}
        stickyHeaders
        responsive
        renderTableFooter={() =>
          hasNextPage ? (
            <div className="xgs-invoice-factoring__load-more">
              <Button
                theme={ButtonThemes.blueInverted}
                size={ButtonSizes.auto}
                type="button"
                spinner={isFetchingNextPage}
                disabled={isFetchingNextPage}
                onClick={() => fetchNextPage()}
              >
                Load more rows
              </Button>
            </div>
          ) : null
        }
      />
    </div>
  );
};

export default BatchItemsTable;
